import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { getIsPaidFeature } from 'selectors/session';

import Box from 'components/Box';
import CheckboxInput from 'components/forms/CheckboxInput';
import { Mobile } from 'components/MediaQuery';
import PaidFeatureIcon from 'components/PaidFeatureIcon';
import Text from 'components/Text';
import TooltipIcon from 'components/TooltipIcon';

import cxHelpers from 'util/className';
import { handleStopPropagation } from 'util/dom';
import { toI18n } from 'util/i18n';

const CHECKBOX_HEIGHT = 24;

@connect((state, props) => ({
  isPaidFeature: getIsPaidFeature(state, { feature: props.premiumFeature }),
}))
@cxHelpers('CheckboxField')
export default class CheckboxField extends PureComponent {
  static propTypes = {
    titleI18n: PropTypes.string,
    titleI18nProps: PropTypes.object,
    descI18n: PropTypes.string,
    descI18nProps: PropTypes.object,
    desc: PropTypes.node,
    hasError: PropTypes.bool,
    errorMessageI18n: PropTypes.string,
    errorMessageI18nProps: PropTypes.object,
    title: PropTypes.node,
    onChange(props, propName) {
      if (
        props.disabled === false &&
        (props[propName] === undefined || typeof props[propName] !== 'function')
      ) {
        return new Error('Please provide an onChange function!');
      }
    },
    value: PropTypes.bool,
    fontSize: PropTypes.string,
    premiumFeature: PropTypes.string,
    noPremiumFeatureIcon: PropTypes.bool,
    children: PropTypes.node,
    descFontSize: PropTypes.string,
    hasNestedInputs: PropTypes.bool,
    titleFontWeight: PropTypes.string,
    titleColor: PropTypes.string,
    isPaidFeature: PropTypes.bool,
    link: PropTypes.string,
    linkI18n: PropTypes.string,
    linkDataTestId: PropTypes.string,
    dataTestId: PropTypes.string,
    tooltip: PropTypes.object,
    premiumIconRightComponent: PropTypes.object,
  };

  static defaultProps = {
    fontSize: 'lg',
    descFontSize: 'fs14',
    titleFontWeight: 'fw400',
    titleColor: 'navy',
  };

  handleClick = () =>
    !this.props.disabled && this.props.onChange(!this.props.value);

  handleClickLink = e => e.stopPropagation();

  render() {
    const {
      title,
      titleI18n,
      titleI18nProps,
      descI18n,
      descI18nProps,
      desc,
      fontSize,
      premiumFeature,
      noPremiumFeatureIcon,
      children,
      descFontSize,
      hasNestedInputs,
      titleFontWeight,
      titleColor,
      isPaidFeature,
      dispatch,
      hasError,
      errorMessageI18n,
      errorMessageI18nProps,
      link,
      linkI18n,
      linkDataTestId,
      dataTestId,
      tooltip,
      disabled,
      premiumIconRightComponent,
      ...props
    } = this.props;

    const textProps = {
      [fontSize]: true,
      [titleFontWeight]: true,
      [titleColor]: true,
    };

    const descProps =
      descI18n && isPaidFeature
        ? {
            [descFontSize]: true,
            neutral600: true,
          }
        : {
            grayDark: true,
            [descFontSize]: true,
            fw400: true,
          };

    const handleRenderTooltip = () => {
      const {
        size = 14,
        color = 'gray-light',
        theme = 'navy',
        iconType = 'questionCircleLiteProIcon',
        preferPlace = 'below',
        dataTestId: tooltipDataTestId,
        content,
      } = tooltip;
      return (
        <Box ml4 tag="span">
          <TooltipIcon
            dataTestId={tooltipDataTestId}
            preferPlace={preferPlace}
            iconType={iconType}
            theme={theme}
            color={color}
            size={size}
            content={content}
          />
        </Box>
      );
    };

    return (
      <Mobile>
        {mobile => (
          <Box
            row
            vtop
            className={this.cx()}
            minh={CHECKBOX_HEIGHT}
            data-testid={dataTestId}
          >
            <Box w={32} mt={hasNestedInputs ? 7 : 0} pl={1}>
              <CheckboxInput
                {...props}
                disabled={disabled}
                onChange={this.handleClick}
              />
            </Box>
            <Box
              grow
              onClick={this.handleClick}
              cursor={disabled ? 'not-allowed' : 'pointer'}
              pt={!(isPaidFeature && !noPremiumFeatureIcon) ? 7 : 3}
            >
              <Box
                vcenter={!mobile}
                mbxxxs
                block={!isPaidFeature}
                style={mobile && hasNestedInputs && { lineHeight: '24px' }}
              >
                {title || (
                  <Box disabled={disabled} tag="span">
                    <Text
                      asHTML
                      i18n={titleI18n}
                      i18nProps={titleI18nProps}
                      purple={isPaidFeature}
                      fw500={!isPaidFeature}
                      fw600={isPaidFeature}
                      {...textProps}
                    />
                  </Box>
                )}
                {link && (
                  <Box
                    onClick={this.handleClickLink}
                    tag="a"
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={linkDataTestId}
                  >
                    {toI18n(linkI18n)}
                  </Box>
                )}

                {tooltip && handleRenderTooltip()}

                <Box inline={mobile}>{children}</Box>

                {isPaidFeature && !noPremiumFeatureIcon && (
                  <Box row vcenter>
                    <PaidFeatureIcon
                      feature={premiumFeature}
                      mlxs
                      iconSize={mobile ? 'xs' : 'sm'}
                    />
                    {premiumIconRightComponent}
                  </Box>
                )}
              </Box>
              {hasError && (
                <Box onClick={handleStopPropagation}>
                  <Text
                    fs14
                    red
                    i18n={errorMessageI18n}
                    i18nProps={errorMessageI18nProps}
                  />
                </Box>
              )}

              {desc ||
                (descI18n && (
                  <Box mt8={isPaidFeature}>
                    <Text
                      {...descProps}
                      i18n={descI18n}
                      i18nProps={descI18nProps}
                    />
                  </Box>
                ))}
            </Box>
          </Box>
        )}
      </Mobile>
    );
  }
}
